import React, { Fragment, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { isEmpty } from 'lodash';
import { Icon } from 'Components/frameworks/icon/Icon';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { ClickOutside } from 'Components/frameworks/click-outside/ClickOutside';
import { ICONS } from 'Components/utils/icon/icons';
import { STATIC_PAGE_TYPES, TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { NavigationLargeElement } from 'Components/widgets/navigation/large/panels/NavigationLargeElement';
import { elements as ELEMENTS } from 'Components/widgets/navigation/Navigation.constants';
import { NavigationContact } from 'Components/widgets/navigation/contact/NavigationContact';
import { NavigationMemberAccessButton } from 'Components/widgets/navigation/member-access/member-access-button/NavigationMemberAccessButton';
import { useSelector } from 'react-redux';
import { NavigationMemberAccess } from 'Components/widgets/navigation/member-access/NavigationMemberAccess';
import { usePageContext } from 'Context/PageContext';
import { t } from 'Src/i18n';
import { getConnectedUserInfoSelector } from 'Store/booking/booking.selector';
export const ConfirmationHeader = ({ contact }: Props) => {
  const { pageType } = usePageContext();
  const [contactPopUpVisible, setContactPopUpVisible] = useState<boolean>(false);
  const [loginPopUpVisible, setLoginPopUpVisible] = useState<boolean>(false);
  const { trackClickEvent } = useTrackEvent();
  const hasContactDetails =
    contact?.hours || contact?.description || contact?.toll || contact?.title;
  const connectedUserInfo = useSelector(getConnectedUserInfoSelector);
  const { isLoggedIn, isQuickLoggedIn } = connectedUserInfo;
  const isBookingEnginePage = pageType === STATIC_PAGE_TYPES.BE;
  const iconButtonClassNames =
    'cursor-pointer flex items-center px-15 relative hocus:text-black-active';
  return (
    <header
      className="border-b-1 border-grey bg-white px-20 md:px-50"
      data-testid="booking-header"
      role="navigation"
    >
      <div className="relative isolate z-1 m-auto flex h-60 max-w-site justify-between gap-20">
        <DynamicLink
          className="flex items-center gap-10 text-14 text-black hocus:text-black-active"
          onClick={() =>
            trackClickEvent(TRACK_EVENT_NAMES.CLICK_HEADER, {
              detail_click: 'back_home',
            })
          }
          url="/"
        >
          <Icon dataTestid="left-arrow-icon" name={ICONS.ARROW_TAIL_LEFT} width="1.875rem" />
          <span className="hidden sm:block">{t('booking.confirmation.goToHome')}</span>
        </DynamicLink>

        <DynamicLink
          className="flex items-center justify-center"
          onClick={() =>
            trackClickEvent(TRACK_EVENT_NAMES.CLICK_HEADER, {
              detail_click: 'logo',
            })
          }
          url="/"
        >
          <Icon
            className="text-black"
            dataTestid="cm-logo"
            isNotSquare
            name={ICONS.CLUBMED}
            width="6.25rem"
          />
        </DynamicLink>

        <div className="relative flex gap-20">
          {!isEmpty(contact) && (
            <Fragment>
              <button
                aria-expanded={ELEMENTS.CONTACT}
                className={classnames('navigation-arrow relative flex items-center', {
                  'navigation-arrow-active': contactPopUpVisible,
                })}
                data-testid="header-contact-button"
                onClick={() => {
                  hasContactDetails && setContactPopUpVisible(true);
                  trackClickEvent(TRACK_EVENT_NAMES.CLICK_HEADER, {
                    detail_click: 'call',
                  });
                }}
                title={contact.title}
                type="button"
              >
                <Icon
                  className="text-black"
                  dataTestid="phone-icon"
                  hasRtl
                  name={ICONS.PHONE_LIGHT}
                  width="1.875rem"
                />
                <span className="sr-only">{contact.title}</span>
              </button>
              {contactPopUpVisible && (
                <ClickOutside
                  className="absolute right-0 top-[66px]"
                  onClick={() => {
                    contactPopUpVisible && setContactPopUpVisible(false);
                  }}
                >
                  <NavigationLargeElement
                    className="w-300 rounded-12 shadow-md"
                    onClose={() => setContactPopUpVisible(false)}
                  >
                    <NavigationContact
                      isBookingEnginePage={isBookingEnginePage}
                      contact={contact}
                    />
                  </NavigationLargeElement>
                </ClickOutside>
              )}
            </Fragment>
          )}
          <div
            className={classnames('navigation-arrow relative flex items-center', {
              'navigation-arrow-active': loginPopUpVisible,
            })}
          >
            <NavigationMemberAccessButton
              className={classnames(iconButtonClassNames)}
              isActive={loginPopUpVisible}
              isAuthenticated={isLoggedIn}
              isQuickLoggedIn={isQuickLoggedIn}
              onClick={() => setLoginPopUpVisible(true)}
              size={1.875}
              title={t('account.authentication.yourAccount.title')}
            />
          </div>
          {loginPopUpVisible && (
            <ClickOutside
              className="absolute right-0 top-[66px]"
              onClick={() => {
                loginPopUpVisible && setLoginPopUpVisible(false);
              }}
            >
              <NavigationLargeElement
                className={classnames('w-[90vw] rounded-12 shadow-md md:w-640', {
                  'w-[18.125rem] p-0': isLoggedIn || isQuickLoggedIn,
                })}
                onClose={() => setLoginPopUpVisible(false)}
              >
                <NavigationMemberAccess userInfo={connectedUserInfo} />
              </NavigationLargeElement>
            </ClickOutside>
          )}
        </div>
      </div>
    </header>
  );
};
